/**
 * Input component type definitions
 * @module shared/types/input.types
 */

/**
 * Input mode enum for mobile keyboard hints
 * @see https://developer.mozilla.org/en-US/docs/Web/HTML/Global_attributes/inputmode
 */
export enum InputMode {
  /**
   * No virtual keyboard (for custom input methods)
   */
  None = 'none',

  /**
   * Standard keyboard (default)
   */
  Text = 'text',

  /**
   * Telephone keypad (with +*# keys)
   */
  Tel = 'tel',

  /**
   * URL keyboard (with .com and / keys)
   */
  Url = 'url',

  /**
   * Email keyboard (with @ and . keys)
   */
  Email = 'email',

  /**
   * Numeric keypad (numbers only, no decimal)
   */
  Numeric = 'numeric',

  /**
   * Decimal keypad (numbers with decimal point)
   */
  Decimal = 'decimal',

  /**
   * Search keyboard (with search action button)
   */
  Search = 'search',
}

/**
 * Type guard to check if a value is a valid InputMode
 */
export function isInputMode(value: unknown): value is InputMode {
  return Object.values(InputMode).includes(value as InputMode);
}

/**
 * Get all InputMode values as an array
 */
export function getAllInputModes(): readonly InputMode[] {
  return Object.values(InputMode);
}

/**
 * Input size variants
 */
export enum InputSize {
  Small = 'sm',
  Default = 'default',
  Large = 'lg',
}

/**
 * Input state variants
 */
export enum InputVariant {
  Default = 'default',
  Error = 'error',
  Success = 'success',
}
